/* LIFX Interactive Packaging Panel — behaviour
   Vanilla ES5-safe JS. No framework, no build step. Shopify-safe.

   The panel renders inside a SHADOW ROOT so the host theme's global styles
   (button min-widths, uppercase labels, svg sizing) cannot reach it. The
   section supplies markup in a <template> and config in a JSON <script>. */

(function () {
  'use strict';

  var NEUTRAL_K = 6500;
  var TAG = 'lifx-panel-root';
  /* Bumped whenever the section markup and this script must ship together.
     The section stamps the same number in data-version. */
  var BUILD = 13;

  function warn(msg) {
    if (window.console && console.warn) console.warn('[LIFX Panel] ' + msg);
  }

  /* Logo link region (no Shopify Markets required).
     1. ?lifx_site=au | ?lifx_site=com forces a site (QA).
     2. IP country from the storefront's /browsing_context_suggestions.json
        (Shopify's own geolocation endpoint). Respects VPNs.
     3. Only if that request fails: device timezone. */
  var AU_URL = 'https://www.lifx.com.au', COM_URL = 'https://www.lifx.com';
  function isAustralianDevice() {
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (/^Australia\//.test(tz)) return true;
    } catch (e) {}
    return false;
  }
  function wireLogoLink(scope) {
    var a = scope.querySelector('[data-logo-link]');
    if (!a) return;
    var force = null;
    try { force = new URLSearchParams(location.search).get('lifx_site'); } catch (e) {}
    if (force === 'au') { a.href = AU_URL; return; }
    if (force === 'com') { a.href = COM_URL; return; }

    var fallback = function () { a.href = isAustralianDevice() ? AU_URL : COM_URL; };
    if (!window.fetch) { fallback(); return; }
    var done = false;
    var timer = setTimeout(function () { if (!done) { done = true; fallback(); } }, 2500);
    fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (d) {
        if (done) return;
        var c = d && d.detected_values && d.detected_values.country;
        var code = c && (c.handle || c.iso_code || c.code);
        if (!code) throw new Error('no country');
        done = true; clearTimeout(timer);
        a.href = String(code).toUpperCase() === 'AU' ? AU_URL : COM_URL;
      })
      .catch(function () { if (!done) { done = true; clearTimeout(timer); fallback(); } });
  }

  /* Approximate RGB white-point for a color temperature (Tanner Helland). */
  function kelvinRGB(kelvin) {
    var k = Math.max(1000, Math.min(40000, kelvin)) / 100;
    var r, g, b;
    r = k <= 66 ? 255 : 329.698727446 * Math.pow(k - 60, -0.1332047592);
    g = k <= 66 ? 99.4708025861 * Math.log(k) - 161.1195681661
                : 288.1221695283 * Math.pow(k - 60, -0.0755148492);
    if (k >= 66) { b = 255; } else if (k <= 19) { b = 0; }
    else { b = 138.5177312231 * Math.log(k - 10) - 305.0447927307; }
    function cl(x) { return Math.max(0, Math.min(255, Math.round(x))); }
    return 'rgb(' + cl(r) + ',' + cl(g) + ',' + cl(b) + ')';
  }

  function tempName(k) {
    if (k < 2300) return 'Candle';
    if (k < 3200) return 'Warm white';
    if (k < 4300) return 'Soft white';
    if (k < 5500) return 'Neutral';
    if (k < 7000) return 'Cool white';
    return 'Daylight';
  }

  function hueName(h) {
    h = ((h % 360) + 360) % 360;
    if (h < 14 || h >= 346) return 'Red';
    if (h < 42) return 'Amber';
    if (h < 66) return 'Yellow';
    if (h < 95) return 'Lime';
    if (h < 152) return 'Green';
    if (h < 192) return 'Teal';
    if (h < 232) return 'Blue';
    if (h < 268) return 'Indigo';
    if (h < 330) return 'Magenta';
    return 'Pink';
  }

  /* ------------------------------------------------------------------ */
  /* Panel behaviour, scoped to a shadow root                            */
  /* ------------------------------------------------------------------ */

  function wire(scope, cfg) {
    var rooms = cfg.rooms || [];
    var scenes = cfg.scenes || {};
    if (!rooms.length) {
      warn('CONFIG HAS NO ROOMS \u2014 the panel cannot start.\n' +
           'sections/lifx-panel.liquid is out of date: it built the room list from\n' +
           'section.blocks, which is always empty for a section rendered statically\n' +
           'via {% section %} in a .liquid template. Re-upload the current section file,\n' +
           'which defines rooms as settings instead.');
      return;
    }

    /* A single missed checkbox should not leave the panel inert: if scene URLs
       exist but no room has “Has scene videos” ticked, assume the first room. */
    var anyScenes = rooms.some(function (r) { return r.scenes; });
    var anySrc = Object.keys(scenes).some(function (k) {
      return k !== 'custom' && scenes[k] && scenes[k].src;
    });
    if (!anyScenes && anySrc) {
      rooms[0].scenes = true;
      warn('no room has “Has scene videos” ticked — defaulting to “' + rooms[0].name + '”.\n' +
           'Tick it on the correct room block in the theme editor to silence this.');
    }

    var q = function (sel) { return scope.querySelector(sel); };
    wireLogoLink(scope);

    var els = {
      roomName: q('[data-room-name]'),
      /* Resolve by CLASS, not [data-dropdown]: older markup also carried a
         data-dropdown state attribute on the panel, and querySelector would
         return that instead of the popover. */
      dropdown: q('.lifx-dropdown'),
      scrim: q('[data-scrim]'),
      roomList: q('[data-room-list]'),
      toggleRoom: q('[data-toggle-room]'),
      /* scope is a shadow root normally, but the panel element itself in
         light-DOM compatibility mode. */
      panel: q('.lifx-panel') ||
             (scope.nodeType === 1 && scope.classList && scope.classList.contains('lifx-panel') ? scope : null),
      inner: q('[data-frame-inner]'),
      img: q('[data-room-img]'),
      vidA: q('[data-vid="a"]'),
      vidB: q('[data-vid="b"]'),
      temp: q('[data-temp-overlay]'),
      bloom: q('[data-bloom]'),
      live: q('[data-live]'),
      liveDot: q('[data-live-dot]'),
      liveText: q('[data-live-text]'),
      valueReadout: q('[data-value-readout]'),
      comingSoon: q('[data-coming-soon]'),
      comingSoonName: q('[data-coming-soon-name]'),
      moods: q('[data-moods]'),
      moodsPh: q('[data-moods-placeholder]'),
      rowTemp: q('[data-row="temp"]'),
      rowHue: q('[data-row="hue"]'),
      hueInput: q('[data-hue-input]'),
      hueReadout: q('[data-hue-readout]'),
      hueSwatchLabel: q('[data-hue-swatch-label]'),
      tempInput: q('[data-temp-input]'),
      tempReadout: q('[data-temp-readout]'),
      tempSwatchLabel: q('[data-temp-swatch-label]'),
      tempSwatchDot: q('[data-temp-swatch-dot]'),
      autoToggle: q('[data-auto-toggle]')
    };

    var required = ['panel', 'inner', 'img', 'vidA', 'vidB', 'temp', 'bloom',
      'live', 'liveDot', 'liveText', 'comingSoon', 'comingSoonName', 'moods', 'moodsPh',
      'rowTemp', 'rowHue', 'hueInput', 'tempInput'];
    /* Optional, so pieces can be hidden without breaking the panel:
       roomName (header now shows a tagline instead of the room),
       dropdown / scrim / roomList / toggleRoom (room picker),
       hueReadout / tempReadout (in-card readouts, now shown in the frame pill),
       hueSwatchLabel, tempSwatchLabel / tempSwatchDot, autoToggle. */
    var missingEls = required.filter(function (k) { return !els[k]; });
    if (missingEls.length) { warn('markup missing: ' + missingEls.join(', ')); return; }

    var moodBtns = [].slice.call(scope.querySelectorAll('[data-mood]'));

    var state = {
      room: rooms[0].name,
      dropdownOpen: false,
      selected: 'evening',
      mode: 'video',
      hue: 0,
      temp: NEUTRAL_K,
      autoOn: true
    };

    /* Each mood parks the color slider at the hue that describes its own scene
       (Evening reads magenta, Movie Night teal). The rotation applied to the
       footage is RELATIVE to that start point, so the slider behaves exactly as
       it does from 0deg: at rest there is no shift, and dragging either way
       rotates smoothly from the scene's own color instead of jumping. */
    function defaultHueFor(id) {
      var sc = scenes[id];
      return (sc && typeof sc.hue === 'number') ? sc.hue : 0;
    }
    function rotationFor(id, hue) {
      /* SIGNED, deliberately not normalised to 0-359. Dragging below the start
         hue must yield -1deg, not 359deg: CSS interpolates hue-rotate
         numerically, so a normalised value would animate 0 -> 359 forward
         through every hue and flash a rainbow sweep. */
      return hue - defaultHueFor(id);
    }

    /* Same idea for colour temperature: each mood parks the CCT slider at the
       Kelvin that describes its own lighting, and the wash is measured as a
       DISTANCE from that start point, so at rest the footage is ungraded. */
    function defaultTempFor(id) {
      var sc = scenes[id];
      return (sc && typeof sc.temp === 'number') ? sc.temp : NEUTRAL_K;
    }
    state.hue = defaultHueFor(state.selected);
    state.temp = defaultTempFor(state.selected);

    var cf = { token: 0, front: null, pool: {} };

    function roomOf(name) {
      for (var i = 0; i < rooms.length; i++) if (rooms[i].name === name) return rooms[i];
      return rooms[0];
    }
    function sceneOf(id) { return scenes[id] || null; }
    function isInteractive() { return !!roomOf(state.room).scenes; }

    /* ---------- video crossfade ---------- */

    function prep(v) {
      if (!v) return v;
      v.muted = true; v.defaultMuted = true; v.loop = true;
      v.playsInline = true; v.setAttribute('webkit-playsinline', '');
      return v;
    }

    function crossfadeTo(src) {
      if (!src) return;
      var A = prep(els.vidA), B = prep(els.vidB);
      var front = cf.front === 'A' ? A : (cf.front === 'B' ? B : null);
      var incoming = front === A ? B : A;
      var outgoing = front;
      var token = ++cf.token;

      if (incoming.getAttribute('src') !== src) incoming.src = src;

      function reveal() {
        if (token !== cf.token) return;
        /* Incoming sits ON TOP and fades in over the still-opaque outgoing, so the
           background still image never shows through mid-transition. */
        incoming.style.zIndex = '2';
        if (outgoing && outgoing !== incoming) outgoing.style.zIndex = '1';
        incoming.style.opacity = '1';
        if (outgoing && outgoing !== incoming) {
          setTimeout(function () {
            if (token !== cf.token) return;
            outgoing.style.opacity = '0';
            try { outgoing.pause(); } catch (e) {}
          }, 640);
        }
        cf.front = incoming === A ? 'A' : 'B';
      }

      function start() {
        if (token !== cf.token) return;
        try { if (incoming.readyState < 2) incoming.load(); } catch (e) {}
        try { var p = incoming.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
        if (incoming.readyState >= 2) { reveal(); return; }
        var onReady = function () {
          incoming.removeEventListener('loadeddata', onReady);
          incoming.removeEventListener('canplay', onReady);
          reveal();
        };
        incoming.addEventListener('loadeddata', onReady);
        incoming.addEventListener('canplay', onReady);
        setTimeout(function () { if (token === cf.token && incoming.readyState >= 1) reveal(); }, 450);
        setTimeout(function () { if (token === cf.token) reveal(); }, 1200);
      }

      start();
      setTimeout(start, 80);
    }

    function hideVideos() {
      cf.token++;
      cf.front = null;
      [els.vidA, els.vidB].forEach(function (v) { v.style.opacity = '0'; });
      setTimeout(function () {
        [els.vidA, els.vidB].forEach(function (v) { try { v.pause(); } catch (e) {} });
      }, 660);
    }

    /* Background-cache the remaining scenes so mood switching is near-instant.
       The full scene set is heavy, so respect the visitor's connection: skip
       entirely on Save-Data or 2g/3g, and fetch metadata only on 4g. */
    function preloadScenes() {
      var nav = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      var mode = 'auto';
      if (nav) {
        if (nav.saveData) return;
        var t = nav.effectiveType || '';
        if (t === 'slow-2g' || t === '2g' || t === '3g') return;
        if (t === '4g') mode = 'metadata';
      }
      Object.keys(scenes).forEach(function (k) {
        var src = scenes[k] && scenes[k].src;
        if (!src || cf.pool[src]) return;
        var v = document.createElement('video');
        v.muted = true; v.preload = mode; v.src = src;
        cf.pool[src] = v;
        try { v.load(); } catch (e) {}
      });
    }

    /* ---------- render ---------- */

    function render() {
      var room = roomOf(state.room);
      var interactive = isInteractive();
      var scene = sceneOf(state.selected);
      var accent = (scene && scene.accent) || '#E2FC52';
      var playing = interactive && state.mode === 'video' && !!(scene && scene.src);
      var useTemp = interactive && (state.selected === 'breakfast' || state.selected === 'school');

      if (els.roomName) els.roomName.textContent = room.name;
      els.panel.setAttribute('data-dropdown-state', state.dropdownOpen ? 'open' : 'closed');
      if (els.dropdown) els.dropdown.hidden = !state.dropdownOpen;
      if (els.scrim) els.scrim.hidden = !state.dropdownOpen;
      if (els.toggleRoom) els.toggleRoom.setAttribute('aria-expanded', state.dropdownOpen ? 'true' : 'false');

      if (els.roomList) {
        [].slice.call(els.roomList.children).forEach(function (btn) {
          btn.setAttribute('aria-selected', btn.getAttribute('data-room') === room.name ? 'true' : 'false');
        });
      }

      var hasArt = !!room.image;
      els.comingSoon.hidden = hasArt;
      els.comingSoonName.textContent = room.name;
      if (hasArt && els.img.getAttribute('src') !== room.image) els.img.setAttribute('src', room.image);

      /* Rotation is relative to the mood's start hue, so resting position means
         no shift. Suppressed entirely in temperature mode. */
      var rot = rotationFor(state.selected, state.hue);
      els.inner.style.filter = (useTemp || rot === 0) ? 'none' : 'hue-rotate(' + rot + 'deg)';

      /* Temperature wash — strength grows with distance from this mood's start. */
      var startTemp = defaultTempFor(state.selected);
      var strength = Math.min(1, Math.abs(state.temp - startTemp) / 3200) * 0.8;
      els.temp.style.background = kelvinRGB(state.temp);
      els.temp.style.opacity = useTemp ? String(strength) : '0';

      els.bloom.style.background = 'radial-gradient(80% 100% at 50% 0%, ' + accent + ', transparent 70%)';
      els.bloom.style.opacity = playing ? '0.5' : '0';

      /* The scene label stays visible for every mood, including Custom (which has
         no clip) — it names the active scene rather than reporting playback. */
      els.live.style.opacity = interactive ? '1' : '0';
      els.liveDot.style.background = accent;
      els.liveDot.style.boxShadow = '0 0 8px ' + accent;
      els.liveText.textContent = 'Live \u00B7 ' + ((scene && scene.label) || '');

      els.moods.hidden = !interactive;
      els.moodsPh.hidden = interactive;
      moodBtns.forEach(function (btn) {
        var id = btn.getAttribute('data-mood');
        var s = sceneOf(id);
        var a = (s && s.accent) || '#E2FC52';
        var on = id === state.selected;
        var ring = btn.querySelector('.lifx-mood__ring');
        var icon = btn.querySelector('.lifx-mood__icon');
        if (ring) {
          ring.style.boxShadow = on
            ? 'inset 0 0 0 1.5px ' + a + ', 0 0 24px -5px ' + a
            : 'inset 0 0 0 1px rgba(255,255,255,0.06)';
          ring.style.background = on
            ? 'linear-gradient(180deg, ' + a + '22, transparent 72%)'
            : 'transparent';
        }
        if (icon) icon.style.stroke = on ? a : 'rgba(251,254,243,0.78)';
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      els.rowTemp.hidden = !useTemp;
      els.rowHue.hidden = useTemp;

      els.hueInput.value = state.hue;
      els.tempInput.value = state.temp;

      var hueText = state.hue + '\u00B0  \u00B7  ' + hueName(state.hue);
      var tempText = state.temp + 'K  \u00B7  ' + tempName(state.temp);
      if (els.hueReadout) els.hueReadout.textContent = hueText;
      if (els.tempReadout) els.tempReadout.textContent = tempText;
      /* Frame pill shows whichever control is active. */
      if (els.valueReadout) els.valueReadout.textContent = useTemp ? tempText : hueText;

      if (els.hueSwatchLabel) els.hueSwatchLabel.textContent = state.hue === 303 ? 'Reset' : 'Magenta';
      if (els.tempSwatchLabel) els.tempSwatchLabel.textContent = state.temp === startTemp ? 'Neutral' : 'Reset';
      if (els.tempSwatchDot) els.tempSwatchDot.style.background = kelvinRGB(state.temp);

      if (els.autoToggle) els.autoToggle.setAttribute('aria-pressed', state.autoOn ? 'true' : 'false');
    }

    /* ---------- actions ---------- */

    function pickMood(id) {
      /* Reset to this mood's own starting point: its scene hue (zero rotation) and
         neutral CCT, so a fresh scene plays ungraded. */
      state.selected = id;
      state.hue = defaultHueFor(id);
      state.temp = defaultTempFor(id);
      var scene = sceneOf(id);
      if (scene && scene.src) {
        state.mode = 'video';
        render();
        crossfadeTo(scene.src);
      } else {
        state.mode = 'still';
        hideVideos();
        render();
      }
    }

    function selectRoom(name) {
      state.room = name;
      state.dropdownOpen = false;
      state.selected = 'evening';
      state.hue = defaultHueFor('evening');
      state.temp = defaultTempFor('evening');
      var room = roomOf(name);
      var scene = sceneOf('evening');
      if (room.scenes && scene && scene.src) {
        state.mode = 'video';
        cf.front = null;
        [els.vidA, els.vidB].forEach(function (v) { v.style.opacity = '0'; });
        render();
        setTimeout(function () { crossfadeTo(scene.src); }, 60);
      } else {
        state.mode = 'still';
        hideVideos();
        render();
      }
    }

    /* ---------- wiring ---------- */

    if (els.roomList) {
      rooms.forEach(function (r) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lifx-roomopt';
        btn.setAttribute('data-room', r.name);
        btn.setAttribute('role', 'option');
        var label = document.createElement('span');
        label.textContent = r.name;
        btn.appendChild(label);
        var svgNS = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('class', 'lifx-roomopt__check');
        svg.setAttribute('width', '18');
        svg.setAttribute('height', '18');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', '#E2FC52');
        svg.setAttribute('stroke-width', '2.4');
        svg.setAttribute('stroke-linecap', 'round');
        svg.setAttribute('stroke-linejoin', 'round');
        var path = document.createElementNS(svgNS, 'path');
        path.setAttribute('d', 'M20 6 9 17l-5-5');
        svg.appendChild(path);
        btn.appendChild(svg);
        btn.addEventListener('click', function () { selectRoom(r.name); });
        els.roomList.appendChild(btn);
      });
    }

    if (els.toggleRoom) {
      els.toggleRoom.addEventListener('click', function () {
        state.dropdownOpen = !state.dropdownOpen;
        render();
      });
    }
    if (els.scrim) {
      els.scrim.addEventListener('click', function () {
        state.dropdownOpen = false;
        render();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.dropdownOpen) { state.dropdownOpen = false; render(); }
    });

    moodBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { pickMood(btn.getAttribute('data-mood')); });
    });

    els.hueInput.addEventListener('input', function (e) {
      state.hue = parseInt(e.target.value, 10) || 0;
      render();
    });
    var hueSwatch = q('[data-hue-swatch]');
    if (hueSwatch) {
      hueSwatch.addEventListener('click', function () {
        state.hue = state.hue === 303 ? defaultHueFor(state.selected) : 303;
        render();
      });
    }
    els.tempInput.addEventListener('input', function (e) {
      state.temp = parseInt(e.target.value, 10) || defaultTempFor(state.selected);
      render();
    });
    var tempSwatch = q('[data-temp-swatch]');
    if (tempSwatch) {
      tempSwatch.addEventListener('click', function () {
        state.temp = defaultTempFor(state.selected);
        render();
      });
    }
    if (els.autoToggle) {
      els.autoToggle.addEventListener('click', function () {
        state.autoOn = !state.autoOn;
        render();
      });
    }

    /* ---------- diagnostics ---------- */

    prep(els.vidA);
    prep(els.vidB);

    [els.vidA, els.vidB].forEach(function (v) {
      v.addEventListener('error', function () {
        var code = v.error ? v.error.code : '?';
        warn('video failed to load (code ' + code + '): ' + (v.currentSrc || v.getAttribute('src')) +
             '\nUpload the .mp4 to Settings \u2192 Files and paste its URL into the section\u2019s Scene videos setting.');
      });
    });

    els.img.addEventListener('error', function () {
      warn('room image failed to load: ' + (els.img.currentSrc || els.img.getAttribute('src')) +
           '\nUpload the room photos to the theme\u2019s assets/ folder.');
      /* Fall back to the placeholder rather than a broken-image glyph. */
      els.img.style.visibility = 'hidden';
      els.comingSoon.hidden = false;
    });

    els.img.addEventListener('load', function () {
      els.img.style.visibility = '';
      if (roomOf(state.room).image) els.comingSoon.hidden = true;
    });

    var noSrc = Object.keys(scenes).filter(function (k) {
      return k !== 'custom' && !(scenes[k] && scenes[k].src);
    });
    if (noSrc.length) warn('no video URL configured for: ' + noSrc.join(', '));

    var noArt = rooms.filter(function (r) { return !r.image; }).map(function (r) { return r.name; });
    if (noArt.length) warn('no room photo for: ' + noArt.join(', '));

    /* ---------- boot ---------- */

    render();

    var first = sceneOf('evening');
    if (isInteractive() && first && first.src) crossfadeTo(first.src);
    setTimeout(preloadScenes, 1200);

    /* Some mobile browsers refuse the very first muted autoplay until the user
       touches the page; retry the active scene once on first interaction. */
    var kicked = false;
    function kick() {
      if (kicked) return;
      kicked = true;
      var s = sceneOf(state.selected);
      if (state.mode === 'video' && s && s.src) {
        [els.vidA, els.vidB].forEach(function (v) {
          if (v.style.opacity === '1') {
            try { var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
          }
        });
      }
    }
    ['touchstart', 'pointerdown', 'click'].forEach(function (ev) {
      document.addEventListener(ev, kick, { once: true, passive: true });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Custom element: builds the shadow root                              */
  /* ------------------------------------------------------------------ */

  function mount(host) {
    if (host.__lifxReady) return;
    host.__lifxReady = true;

    var tpl = host.querySelector('template[data-lifx-template]');
    var cfgEl = host.querySelector('script[data-lifx-config]');
    if (!tpl || !cfgEl) { warn('template or config script missing inside <' + TAG + '>'); return; }

    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); }
    catch (e) { warn('config JSON is invalid: ' + e.message); return; }

    var cssHref = host.getAttribute('data-css');

    var markupBuild = parseInt(host.getAttribute('data-version'), 10);
    if (!isNaN(markupBuild) && markupBuild !== BUILD) {
      warn('VERSION MISMATCH \u2014 the panel will not work correctly.\n' +
           '  sections/lifx-panel.liquid is build ' + markupBuild + '\n' +
           '  assets/lifx-panel.js is build ' + BUILD + '\n' +
           'Re-upload whichever is older. These two files must always ship together.');
    }
    var shadow = host.attachShadow ? host.attachShadow({ mode: 'open' }) : null;

    if (!shadow) {
      /* No shadow DOM support (very old browser): render in light DOM and
         accept that theme CSS may bleed. */
      warn('shadow DOM unavailable; rendering in light DOM');
      if (cssHref) {
        var l = document.createElement('link');
        l.rel = 'stylesheet'; l.href = cssHref;
        document.head.appendChild(l);
      }
      host.appendChild(tpl.content.cloneNode(true));
      wire(host, cfg);
      return;
    }

    if (cssHref) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssHref;
      /* Avoid a flash of unstyled markup while the sheet loads. */
      host.style.visibility = 'hidden';
      var reveal = function () { host.style.visibility = ''; };
      link.addEventListener('load', reveal);
      link.addEventListener('error', function () {
        warn('stylesheet failed to load: ' + cssHref);
        reveal();
      });
      setTimeout(reveal, 3000);
      shadow.appendChild(link);
    }

    shadow.appendChild(tpl.content.cloneNode(true));
    wire(shadow, cfg);
  }

  /* ------------------------------------------------------------------ */
  /* Light-DOM compatibility boot                                        */
  /*                                                                     */
  /* If sections/lifx-panel.liquid has not been re-uploaded yet it still  */
  /* emits flat markup with no <lifx-panel-root> wrapper. Wire that up    */
  /* anyway so the panel is functional, and say so loudly.                */
  /* ------------------------------------------------------------------ */

  function bootLegacy() {
    if (document.querySelector(TAG)) return; // current markup present, nothing to do
    var panels = [].slice.call(document.querySelectorAll('[data-lifx-panel], .lifx-panel'));
    panels.forEach(function (el) {
      if (el.__lifxReady) return;
      var cfgEl = el.querySelector('script[data-lifx-config]') ||
                  document.querySelector('script[data-lifx-config]');
      if (!cfgEl) return;
      var cfg;
      try { cfg = JSON.parse(cfgEl.textContent); }
      catch (e) { warn('config JSON is invalid: ' + e.message); return; }
      el.__lifxReady = true;
      warn('running in LIGHT-DOM COMPATIBILITY MODE.\n' +
           'sections/lifx-panel.liquid is out of date \u2014 re-upload it to get shadow-DOM\n' +
           'isolation from the theme\u2019s global CSS. The panel will work either way.');
      wire(el, cfg);
    });
  }

  if ('customElements' in window) {
    if (!customElements.get(TAG)) {
      var El = function () { return Reflect.construct(HTMLElement, [], El); };
      El.prototype = Object.create(HTMLElement.prototype);
      El.prototype.constructor = El;
      Object.setPrototypeOf(El, HTMLElement);
      El.prototype.connectedCallback = function () { mount(this); };
      customElements.define(TAG, El);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootLegacy);
    else bootLegacy();
    document.addEventListener('shopify:section:load', bootLegacy);
  } else {
    var boot = function () {
      [].slice.call(document.getElementsByTagName(TAG)).forEach(mount);
      bootLegacy();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', boot);
  }

  window.LIFXPanel = { mount: mount, bootLegacy: bootLegacy };
})();